// eslint-disable-next-line no-restricted-imports
import {useChatState} from '@github-ui/copilot-chat/CopilotChatContext'
import {CreateIssueForm as InnerCreateIssueForm} from '@github-ui/issue-create/CreateIssueForm'
import {useIssueCreateActions, useIssueCreateMetadata} from '@github-ui/issue-create/IssueCreateDataContext'
import {instanceOfIssueFormData, type IssueCreatePayload} from '@github-ui/issue-create/Model'
import type {ChangeTrackingProps, IssueFormRef} from '@github-ui/issue-form/Types'
import {IssueViewerLoading} from '@github-ui/issue-viewer/IssueViewerLoading'
import {noop} from '@github-ui/noop'
import {IssueDraftIcon} from '@primer/octicons-react'
import {type RefObject, useCallback, useEffect, useState} from 'react'

import {userMessageIsTimelineEvent} from '../../timeline-events/TimelineEvents'
import type {DraftIssue} from '../content-preview-types'
import {FailureState} from '../FailureState'
import {RelationshipsSection} from './relationships-section/RelationshipsSection'
import {useDraftIssueAssignees} from './use-draft-issue-assignees'
import {useDraftIssueIssueFields} from './use-draft-issue-issue-fields'
import {useDraftIssueLabels} from './use-draft-issue-labels'
import {useDraftIssueMilestone} from './use-draft-issue-milestone'
import {useDraftIssueProjects} from './use-draft-issue-projects'
import type {TreeNode} from './use-draft-issue-tree-map'
import {useDraftIssueType} from './use-draft-issue-type'
import {useParentMessageRetryCallback} from './use-parent-message-retry-callback'

type CreateIssueFormProps = {
  draftIssue: DraftIssue
  issueNode?: TreeNode<DraftIssue>
  template?: IssueCreatePayload
  issueFormRef: RefObject<IssueFormRef | null>
  loadingDraftIssueRepo?: boolean
  onChange?: (issue: Partial<DraftIssue>) => void
  onClose?: () => void
  onBeforeCreate?: (issueBody: string) => Promise<string>
} & ChangeTrackingProps

export function CreateIssueForm({
  draftIssue,
  issueNode,
  template,
  issueFormRef,
  loadingDraftIssueRepo,
  onChange,
  onClose,
  onBeforeCreate,
  onFieldChange,
}: CreateIssueFormProps) {
  const {name: initTitle, body: initDescription = '', defaultValuesById} = draftIssue

  const {repository} = useIssueCreateMetadata()
  const {clearSessionData} = useIssueCreateActions()
  const [title, setTitle] = useState(initTitle)
  const [description, setDescription] = useState(initDescription)

  const onRetry = useParentMessageRetryCallback()
  const state = useChatState()

  const onClearSession = useCallback(() => {
    // Copied from packages/issue-create/hooks/use-safe-close.ts
    clearSessionData()
    issueFormRef.current?.clearSessionStorage()
  }, [clearSessionData, issueFormRef])

  const onClearAndClose = useCallback(() => {
    onClearSession()
    onClose?.()
  }, [onClearSession, onClose])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setTitle(initTitle)
    setDescription(initDescription)
  }, [initDescription, initTitle])

  useDraftIssueAssignees(draftIssue)
  useDraftIssueLabels(draftIssue)
  useDraftIssueType(draftIssue)
  useDraftIssueMilestone(draftIssue)
  useDraftIssueProjects(draftIssue)
  useDraftIssueIssueFields(draftIssue)

  if (repository == null) {
    if ((draftIssue.repository === undefined && draftIssue.isStreaming) || loadingDraftIssueRepo) {
      return <IssueViewerLoading optionConfig={{useViewportQueries: false}} />
    }
    return (
      <FailureState
        title="Repository required"
        description="Please select a repository above to continue creating your issue."
        icon={IssueDraftIcon}
      />
    )
  }

  // Render loading skeleton when switching templates and repos until Copilot responds with the updated data.
  // The old repo/template/form is no longer applicaple to the pending draft, and the user may not realize they're
  // waiting on Copilot if they don't see the timeline event
  const latestMessage = state.messages.at(-1)
  if (latestMessage && userMessageIsTimelineEvent(latestMessage, ['switch-issue-template', 'switch-issue-repo'])) {
    return <IssueViewerLoading optionConfig={{useViewportQueries: false}} />
  }

  // It's possible for Copilot to fail to return data in the right format, in which case we allow one retry.
  // We only want to do a retry if the following conditions are met:
  // - The template is a Form
  // - The message is from Copilot
  // - defaultValuesById is empty (Copilot should return Form field values)
  // - The message is complete (not still streaming)
  // - The message is the most recent message (don't retry old messages)
  // - The parent message is not a Retry (limit to one retry)
  const message = state.messages.find(m => m.id === draftIssue.messageId)
  const parentMessage =
    message?.parentMessageIndex !== undefined ? state.messages[message.parentMessageIndex] : undefined
  if (message && parentMessage) {
    const isIssueForm = template && instanceOfIssueFormData(template.data)
    const isNoData = Object.keys(defaultValuesById || {}).length === 0
    const messageCompleted = !state.isWaitingOnCopilot
    const messageIsLastMessage = message.messageIndex === state.messages.length - 1
    const isCopilotMessage = message.role === 'assistant'
    const hasRetried = userMessageIsTimelineEvent(parentMessage, ['automatic-retry'])
    if (isIssueForm && isNoData) {
      if (!messageCompleted) {
        return <IssueViewerLoading optionConfig={{useViewportQueries: false}} />
      } else if (messageIsLastMessage && isCopilotMessage && !hasRetried) {
        void onRetry(
          message,
          'Copilot failed to use the issue template, retrying…',
          "Force a retry of the user's request and make sure to generate template form values properly. Do not send the same response as the previous attempt.",
        )
        return <IssueViewerLoading optionConfig={{useViewportQueries: false}} />
      } else {
        return (
          <FailureState
            title="Copilot failed to create an issue"
            description="Copilot failed to use the issue template. Please retry or choose a different template."
            icon={IssueDraftIcon}
          />
        )
      }
    }
  }

  return (
    <>
      <InnerCreateIssueForm
        repository={repository}
        title={title}
        body={description}
        setTitle={newTitle => {
          setTitle(newTitle)
          onChange?.({name: newTitle})
        }}
        setBody={newDescription => {
          setDescription(newDescription)
          onChange?.({body: newDescription})
        }}
        clearOnCreate={onClearAndClose}
        issueFormRef={issueFormRef}
        onCreateSuccess={noop}
        onCreateError={noop}
        onCancel={onClearAndClose}
        onBeforeCreate={onBeforeCreate}
        defaultValuesById={defaultValuesById}
        selectedTemplate={template}
        onFieldChange={onFieldChange}
      />
      {issueNode && <RelationshipsSection issueNode={issueNode} />}
    </>
  )
}
